/**
 * The sounds a call makes in the app.
 *
 * Three of them, all synthesised — there is no audio file to ship, cache or
 * fail to load, and a call must never be silent because a request 404'd.
 *
 * ── why a call needs them ──────────────────────────────────────────────────
 *
 * Pressing "call" and hearing nothing tells you nothing: not whether it is
 * ringing, not whether the app froze, not whether the other phone is even
 * reachable. Every telephone ever built answers that question with a tone, and
 * people read those tones without being taught — a slow pulse means "it is
 * ringing", a fast one means "they are busy", silence means "something is
 * wrong with this call". So:
 *
 *   ringback  a slow 1 s pulse while the other side is being rung
 *   busy      a fast 0.35 s stutter when they decline or do not answer
 *   incoming  Pine's own ringtone on this side while a call is arriving —
 *             the same one the Android app rings with — and a two-note
 *             chime only if the file cannot play
 *
 * 425 Hz is the European call-progress frequency, the pitch a landline makes,
 * which is exactly why it reads as a telephone rather than as a notification.
 *
 * Everything routes through one AudioContext, so on a phone these follow the
 * call's own audio route: the ringback comes out of the earpiece you are
 * already holding to your head, not the loudspeaker.
 */

const RINGBACK_HZ = 425
const CHIME = [660, 880]

/*
 * The ringtone, as a file next to the bundle.
 *
 * Written as new URL(…, import.meta.url) rather than an import: the bundler
 * turns this form into the hashed asset path for the web and desktop builds,
 * and Node — which loads this module in the tests — sees nothing but a URL it
 * never fetches.
 */
const RINGTONE_URL = new URL('../assets/ringtone.mp3', import.meta.url).href

let context = null
let timer = null
let stopAt = null
let ringing = null // the <audio> playing the ringtone, while it plays

function audio () {
  if (context && context.state !== 'closed') return context
  try {
    context = new (window.AudioContext || window.webkitAudioContext)()
  } catch {
    context = null // no WebAudio here; the call still works, just quietly
  }
  return context
}

/**
 * One burst of tone.
 *
 * Ramped in and out over 15 ms rather than switched: a square edge on a sine
 * is a click, and a click on every pulse of a ringback is worse than no
 * ringback at all.
 */
function beep ({ freq = RINGBACK_HZ, at = 0, length = 1, gain = 0.12 } = {}) {
  const ctx = audio()
  if (!ctx) return
  const start = ctx.currentTime + at

  const osc = ctx.createOscillator()
  const volume = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq

  volume.gain.setValueAtTime(0, start)
  volume.gain.linearRampToValueAtTime(gain, start + 0.015)
  volume.gain.setValueAtTime(gain, start + Math.max(0.02, length - 0.015))
  volume.gain.linearRampToValueAtTime(0, start + length)

  osc.connect(volume).connect(ctx.destination)
  osc.start(start)
  osc.stop(start + length + 0.02)
}

function prime () {
  const ctx = audio()
  if (!ctx) return null
  // A suspended context is the autoplay policy: a call that arrives while the
  // page has never been touched cannot make a sound until it is. Asking costs
  // nothing and usually works, because starting a call *is* a gesture.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {})
  return ctx
}

function loop (fn, every) {
  stop()
  if (!prime()) return
  fn()
  timer = setInterval(fn, every)
}

export const tones = {
  /** While the other side is being rung: a slow pulse, like a phone line. */
  ringback () {
    loop(() => beep({ length: 1 }), 4000)
  },

  /**
   * Declined, or nobody picked up. Three stutters and then silence — it says
   * "that is over" and then gets out of the way, so it is deliberately not a
   * loop: nothing would be left to stop it once the call surface has gone.
   */
  busy () {
    stop()
    if (!prime()) return
    for (let i = 0; i < 3; i++) beep({ at: i * 0.7, length: 0.35, gain: 0.11 })
    stopAt = setTimeout(() => { stopAt = null }, 2400)
  },

  /**
   * A call arriving, on this side: the ringtone, looping.
   *
   * A browser may refuse to start audio on a page nobody has touched yet — the
   * autoplay rule — and a file can fail to load. Either way the call must not
   * arrive in silence, so the synthesised chime takes over.
   */
  incoming () {
    stop()
    if (typeof Audio === 'undefined') return chime()
    try {
      const audio = new Audio(RINGTONE_URL)
      audio.loop = true
      audio.volume = 0.9
      ringing = audio
      const started = audio.play()
      if (started && started.catch) {
        started.catch(() => {
          if (ringing === audio) {
            ringing = null
            chime()
          }
        })
      }
    } catch {
      chime()
    }
  },

  stop
}

function chime () {
  loop(() => {
    CHIME.forEach((freq, i) => beep({ freq, at: i * 0.18, length: 0.16, gain: 0.1 }))
  }, 2400)
}

function stop () {
  if (ringing) {
    try {
      ringing.pause()
      ringing.removeAttribute('src')
      ringing.load()
    } catch { /* already gone */ }
    ringing = null
  }
  if (timer) clearInterval(timer)
  timer = null
  if (stopAt) clearTimeout(stopAt)
  stopAt = null

  /*
   * The context is closed, not merely left alone.
   *
   * Every oscillator already stops itself — but the busy tone schedules its
   * three bursts up front, and a call answered halfway through them would go
   * on stuttering over the top of somebody's voice. Closing is the one thing
   * that silences audio that has been scheduled and has not started. Building
   * the next one costs about a millisecond.
   */
  if (context) {
    try { context.close() } catch { /* already gone */ }
    context = null
  }
}
